import {
  faAdd,
  faBracketsCurly,
  faPenToSquare,
  faWandMagicSparkles,
} from "@fortawesome/pro-regular-svg-icons";
import { capitalize, noop } from "lodash-es";

import { Button } from "src/base-components/Button";
import { useDatasets } from "src/datasets/api/queries";
import { DatasetSelector } from "src/datasets/DatasetSelector";
import { EmptyState } from "src/design-system/EmptyState";
import { Tooltip } from "src/design-system/Tooltip";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { SchemaOptions } from "src/router/SearchParams";

type Props = {
  type: SchemaOptions;
  handleAddField: () => void;
  isReadonly: boolean;
  onJsonSchemaClick: () => void;
  onManifestNodeBuilderClick?: () => void;
};

const SchemaEmptyStateFlaggedActions: React.FC<{
  type: SchemaOptions;
  isReadonly: boolean;
  onManifestNodeBuilderClick?: () => void;
}> = ({ type, isReadonly, onManifestNodeBuilderClick }) => {
  const { workspace, flow } = useAuthoringContext();
  const datasets = useDatasets({
    flowId: flow.id,
    baseUrl: workspace.base_url,
  });
  const outputSchemaTypingInformation = false;
  const isManifestBuilderEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.manifestBuilder,
  );
  const isLearnSchemaEnabled = isFeatureFlagEnabled(FEATURE_FLAGS.learnSchema);

  if (
    !isLearnSchemaEnabled &&
    !(isManifestBuilderEnabled && onManifestNodeBuilderClick)
  ) {
    return null;
  }

  return (
    <div className="mt-2 flex flex-col items-center gap-y-2">
      {isLearnSchemaEnabled && (
        <>
          {type === SchemaOptions.Input ? (
            <DatasetSelector
              datasets={datasets.data || []}
              isLoading={datasets.isLoading}
              selectedDatasetId={null}
              onSelectData={noop}
            />
          ) : (
            <div className="w-60">
              <Tooltip
                disabled={outputSchemaTypingInformation}
                placement="bottom"
                title="Test run required to generate Output Schema fields"
                asChild
              >
                <Button
                  dataLoc="add-schema-field"
                  disabled={isReadonly || !outputSchemaTypingInformation}
                  iconLeft={faWandMagicSparkles}
                  variant="secondary"
                  fullWidth
                >
                  Generate Output Schema
                </Button>
              </Tooltip>
            </div>
          )}
        </>
      )}
      {isManifestBuilderEnabled && onManifestNodeBuilderClick && (
        <div className="w-60">
          <Tooltip
            title="Interactive schema builder with live UI preview"
            asChild
          >
            <Button
              dataLoc="manifest-node-builder-empty-button"
              disabled={isReadonly}
              iconLeft={faPenToSquare}
              variant="secondary"
              fullWidth
              onClick={onManifestNodeBuilderClick}
            >
              Manifest Node Builder
            </Button>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export const SchemaEmptyState: React.FC<Props> = (props) => {
  const description = (() => {
    if (isFeatureFlagEnabled(FEATURE_FLAGS.learnSchema)) {
      return `No fields added for the ${capitalize(
        props.type,
      )} Schema yet. Click below to add a field or select a dataset to generate Schema`;
    }

    return ` Click below to start creating your ${props.type} schema by adding a field or generate it from JSON.`;
  })();

  return (
    <>
      <EmptyState
        actionButton={[
          {
            label: "Add field",
            icon: faAdd,
            isDisabled: props.isReadonly,
            onClick: () => props.handleAddField(),
            dataLoc: "add-schema-field",
            tooltip: {
              title:
                "This option is perfect for simple schemas with straightforward field types",
            },
          },
          {
            label: "Generate from JSON schema",
            isDisabled: props.isReadonly,
            onClick: () => props.onJsonSchemaClick(),
            dataLoc: "schema-json-view-empty-button",
            tooltip: {
              title: "Create complex nested schemas from existing JSON schema",
            },
          },
        ]}
        description={description}
        headline={`Create ${props.type} schema`}
        icon={faBracketsCurly}
      />

      <SchemaEmptyStateFlaggedActions
        isReadonly={props.isReadonly}
        type={props.type}
        onManifestNodeBuilderClick={props.onManifestNodeBuilderClick}
      />
    </>
  );
};
